import React, { useState, useEffect } from 'react';
/* MODIFICADO: Importado useTheme para suportar tema dark no modal */
/* ANTES: Não havia consumo do contexto de tema */
import { useTheme } from '../../contexts/ThemeContext';
import { Users, Image, Settings, AlertCircle, Check, ArrowLeft, ArrowRight } from 'lucide-react';
import { CreateGroupFormData, FormErrors, EPHEMERAL_OPTIONS } from '../../types/groupTypes';
import { isValidPhone, formatPhoneToJid } from '../../services/groupsApi';

// ============================================================================
// INTERFACES
// ============================================================================

interface GroupFormProps {
  onSubmit: (data: CreateGroupFormData) => Promise<void>;
  onCancel: () => void;
  availableInstances: Array<{ name: string; displayName?: string }>;
  loading?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const GroupForm: React.FC<GroupFormProps> = ({
  onSubmit,
  onCancel,
  availableInstances,
  loading = false
}) => {
  /* MODIFICADO: Hook do tema para ajustar cores */
  /* ANTES: Componente não considerava o tema */
  const { theme } = useTheme();
  // Estado do formulário
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [formData, setFormData] = useState<CreateGroupFormData>({
    instanceName: '',
    subject: '',
    description: '',
    participants: [],
    isAnnouncement: false,
    isLocked: false,
    ephemeralDuration: 0,
    pictureUrl: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [participantInput, setParticipantInput] = useState('');

  // Auto-selecionar primeira instância
  useEffect(() => {
    if (availableInstances.length > 0 && !formData.instanceName) {
      setFormData(prev => ({ ...prev, instanceName: availableInstances[0].name }));
    }
  }, [availableInstances]);

  // Validar step atual
  const validateStep = (currentStep: number): boolean => {
    const newErrors: FormErrors = {};

    if (currentStep === 1) {
      if (!formData.instanceName) {
        newErrors.instanceName = 'Selecione uma instância';
      }
      if (!formData.subject.trim()) {
        newErrors.subject = 'Digite o nome do grupo';
      } else if (formData.subject.length < 3) {
        newErrors.subject = 'Nome deve ter pelo menos 3 caracteres';
      } else if (formData.subject.length > 50) {
        newErrors.subject = 'Nome deve ter no máximo 50 caracteres';
      }
      if (formData.description && formData.description.length > 500) {
        newErrors.description = 'Descrição deve ter no máximo 500 caracteres';
      }
    }

    if (currentStep === 2) {
      if (formData.participants.length < 1) {
        newErrors.participants = 'Adicione pelo menos 1 participante';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Avançar para próximo step
  const nextStep = () => {
    if (validateStep(step)) {
      setStep((prev) => Math.min(3, prev + 1) as 1 | 2 | 3);
    }
  };

  // Voltar para step anterior
  const prevStep = () => {
    setStep((prev) => Math.max(1, prev - 1) as 1 | 2 | 3);
  };

  // Adicionar participante
  const addParticipant = () => {
    const phone = participantInput.trim();
    
    if (!phone) return;

    if (!isValidPhone(phone)) {
      setErrors({ participants: 'Número de telefone inválido' });
      return;
    }

    const jid = formatPhoneToJid(phone);

    if (formData.participants.includes(jid)) {
      setErrors({ participants: 'Este número já foi adicionado' });
      return;
    }

    setFormData(prev => ({
      ...prev,
      participants: [...prev.participants, jid]
    }));

    setParticipantInput('');
    setErrors({});
  };

  // Remover participante
  const removeParticipant = (jid: string) => {
    setFormData(prev => ({
      ...prev,
      participants: prev.participants.filter(p => p !== jid)
    }));
  };

  // Submeter formulário
  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Erro ao criar grupo:', error);
    }
  };

  return (
    /* MODIFICADO: Container do modal com variáveis do tema */
    /* ANTES: className="bg-white" */
    <div
      className="rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
    >
      {/* Header */}
      {/* MODIFICADO: Header com borda e cores do tema */}
      {/* ANTES: border-b border-gray-200, text-gray-900 */}
      <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
        <h2 className="text-xl font-semibold text-n-slate-12">Criar Novo Grupo</h2>
        
        {/* Progress bar */}
        {/* MODIFICADO: Barra de progresso com cores adaptadas */}
        {/* ANTES: barras cinzas fixas */}
        <div className="mt-4 flex items-center gap-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex-1 flex items-center gap-2">
              {/* MODIFICADO: ativo azul, inativo usa cor dependente do tema */}
              {/* ANTES: bg-gray-200/bg-gray-300 */}
              <div
                className={`h-2 flex-1 rounded-full transition-colors ${step >= s ? '' : ''}`}
                style={{ backgroundColor: step >= s ? '#2563eb' : (theme === 'dark' ? '#3f3f46' : '#e5e7eb') }}
              />
              {s < 3 && (
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: theme === 'dark' ? '#52525b' : '#d1d5db' }}
                />
              )}
            </div>
          ))}
        </div>
        
        <div className="mt-2 flex justify-between text-sm font-normal">
          <span style={{ color: step === 1 ? '#2563eb' : 'var(--text-secondary)' }}>
            1. Informações Básicas
          </span>
          <span style={{ color: step === 2 ? '#2563eb' : 'var(--text-secondary)' }}>
            2. Participantes
          </span>
          <span style={{ color: step === 3 ? '#2563eb' : 'var(--text-secondary)' }}>
            3. Configurações
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        {/* STEP 1: Informações Básicas */}
        {step === 1 && (
          <div className="space-y-4">
            {/* Instância */}
            <div>
              {/* MODIFICADO: Label com cor do tema */}
              {/* ANTES: text-gray-700 */}
              <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                Instância WhatsApp *
              </label>
              <select
                value={formData.instanceName}
                onChange={(e) => setFormData({ ...formData, instanceName: e.target.value })}
                /* MODIFICADO: Select com variáveis de tema */
                /* ANTES: border-gray-300 */
                className={`w-full px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-normal`}
                style={{
                  backgroundColor: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  border: `1px solid ${errors.instanceName ? '#ef4444' : 'var(--border-color)'}`
                }}
              >
                <option value="">Selecione uma instância</option>
                {availableInstances.map((instance) => (
                  <option key={instance.name} value={instance.name}>
                    {instance.displayName || instance.name}
                  </option>
                ))}
              </select>
              {errors.instanceName && (
                <p className="mt-1 text-xs font-normal flex items-center gap-1" style={{ color: theme === 'dark' ? '#fecaca' : '#dc2626' }}>
                  <AlertCircle className="w-4 h-4" />
                  {errors.instanceName}
                </p>
              )}
            </div>

            {/* Nome do Grupo */}
            <div>
              {/* MODIFICADO: Label com cor do tema */}
              {/* ANTES: text-gray-700 */}
              <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                Nome do Grupo *
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Ex: Equipe de Vendas 2025"
                maxLength={50}
                /* MODIFICADO: Input com variáveis de tema */
                /* ANTES: border-gray-300 */
                className={`w-full px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-normal`}
                style={{
                  backgroundColor: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  border: `1px solid ${errors.subject ? '#ef4444' : 'var(--border-color)'}`
                }}
              />
              <div className="mt-1 flex justify-between items-center">
                {errors.subject ? (
                  <p className="text-xs font-normal flex items-center gap-1" style={{ color: theme === 'dark' ? '#fecaca' : '#dc2626' }}>
                    <AlertCircle className="w-4 h-4" />
                    {errors.subject}
                  </p>
                ) : (
                  <p className="text-xs font-normal" style={{ color: 'var(--text-secondary)' }}>Mínimo 3 caracteres</p>
                )}
                <span className="text-xs font-normal" style={{ color: 'var(--text-secondary)' }}>{formData.subject.length}/50</span>
              </div>
            </div>

            {/* Descrição */}
            <div>
              {/* MODIFICADO: Label com cor do tema */}
              {/* ANTES: text-gray-700 */}
              <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                Descrição (opcional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descreva o propósito do grupo..."
                rows={4}
                maxLength={500}
                /* MODIFICADO: Textarea com variáveis de tema */
                /* ANTES: border-gray-300 */
                className={`w-full px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm font-normal`}
                style={{
                  backgroundColor: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  border: `1px solid ${errors.description ? '#ef4444' : 'var(--border-color)'}`
                }}
              />
              <div className="mt-1 flex justify-between items-center">
                {errors.description ? (
                  <p className="text-xs font-normal flex items-center gap-1" style={{ color: theme === 'dark' ? '#fecaca' : '#dc2626' }}>
                    <AlertCircle className="w-4 h-4" />
                    {errors.description}
                  </p>
                ) : (
                  <div />
                )}
                <span className="text-xs font-normal" style={{ color: 'var(--text-secondary)' }}>{formData.description.length}/500</span>
              </div>
            </div>

            {/* URL da Foto */}
            <div>
              {/* MODIFICADO: Label com cor do tema */}
              {/* ANTES: text-gray-700 */}
              <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                URL da Foto do Grupo (opcional)
              </label>
              <div className="flex gap-2">
                <Image className="w-10 h-10 flex-shrink-0 mt-1" style={{ color: theme === 'dark' ? '#a1a1aa' : '#9ca3af' }} />
                <input
                  type="url"
                  value={formData.pictureUrl}
                  onChange={(e) => setFormData({ ...formData, pictureUrl: e.target.value })}
                  placeholder="https://exemplo.com/foto.jpg"
                  /* MODIFICADO: Input com variáveis de tema */
                  /* ANTES: border-gray-300 */
                  className="flex-1 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-normal"
                  style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                />
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: Participantes */}
        {step === 2 && (
          <div className="space-y-4">
            {/* Adicionar participante */}
            <div>
              {/* MODIFICADO: Label com cor do tema */}
              <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                Adicionar Participantes *
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={participantInput}
                  onChange={(e) => setParticipantInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addParticipant()}
                  placeholder="5511999999999"
                  /* MODIFICADO: Input com variáveis de tema */
                  className={`flex-1 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-normal`}
                  style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: `1px solid ${errors.participants ? '#ef4444' : 'var(--border-color)'}` }}
                />
                <button
                  onClick={addParticipant}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Adicionar
                </button>
              </div>
              {errors.participants && (
                <p className="mt-1 text-xs font-normal flex items-center gap-1" style={{ color: theme === 'dark' ? '#fecaca' : '#dc2626' }}>
                  <AlertCircle className="w-4 h-4" />
                  {errors.participants}
                </p>
              )}
              <p className="mt-1 text-xs font-normal" style={{ color: 'var(--text-secondary)' }}>
                Digite o número completo (Ex: 5511999999999)
              </p>
            </div>

            {/* Lista de participantes */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Participantes Adicionados ({formData.participants.length})
                </label>
                {formData.participants.length >= 1 && (
                  <span className="text-xs font-normal text-green-600 flex items-center gap-1">
                    <Check className="w-4 h-4" />
                    Mínimo atingido
                  </span>
                )}
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-3" style={{ borderColor: 'var(--border-color)' }}>
                {formData.participants.length === 0 ? (
                  <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                    <Users className="w-12 h-12 mx-auto mb-2" style={{ color: theme === 'dark' ? '#a1a1aa' : '#9ca3af' }} />
                    <p className="text-sm font-normal">Nenhum participante adicionado</p>
                    <p className="text-xs font-normal mt-1">Adicione pelo menos 1 participante</p>
                  </div>
                ) : (
                  formData.participants.map((jid) => (
                    <div
                      key={jid}
                      className="flex items-center justify-between p-3 rounded-lg transition-colors"
                      style={{ backgroundColor: 'transparent', border: '1px solid var(--border-color)' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = 'var(--bg-secondary)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent'; }}
                    >
                      <span className="font-mono text-sm font-normal" style={{ color: 'var(--text-primary)' }}>{jid}</span>
                      <button
                        onClick={() => removeParticipant(jid)}
                        className="text-red-600 hover:text-red-700 text-xs font-normal"
                      >
                        Remover
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: Configurações */}
        {step === 3 && (
          <div className="space-y-6">
            {/* MODIFICADO: Box informativo com cores do tema */}
            {/* ANTES: bg-blue-50 e textos fixos */}
            <div className="rounded-lg p-4" style={{ backgroundColor: theme === 'dark' ? 'rgba(59,130,246,0.08)' : '#eff6ff', border: `1px solid ${theme === 'dark' ? 'rgba(59,130,246,0.25)' : '#bfdbfe'}` }}>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: theme === 'dark' ? '#dbeafe' : '#1e3a8a' }}>
                <Settings className="w-4 h-4" />
                Configurações do Grupo
              </h4>
              <p className="text-sm font-normal" style={{ color: theme === 'dark' ? '#dbeafe' : '#1e40af' }}>
                Defina as permissões e configurações de privacidade do grupo.
              </p>
            </div>

            {/* Modo Anúncio */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="isAnnouncement"
                checked={formData.isAnnouncement}
                onChange={(e) => setFormData({ ...formData, isAnnouncement: e.target.checked })}
                className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex-1">
                <label htmlFor="isAnnouncement" className="text-sm font-semibold cursor-pointer" style={{ color: 'var(--text-primary)' }}>
                  Modo Anúncio
                </label>
                <p className="text-sm font-normal mt-1" style={{ color: 'var(--text-secondary)' }}>
                  Apenas administradores podem enviar mensagens no grupo
                </p>
              </div>
            </div>

            {/* Grupo Travado */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="isLocked"
                checked={formData.isLocked}
                onChange={(e) => setFormData({ ...formData, isLocked: e.target.checked })}
                className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex-1">
                <label htmlFor="isLocked" className="text-sm font-semibold cursor-pointer" style={{ color: 'var(--text-primary)' }}>
                  Grupo Travado
                </label>
                <p className="text-sm font-normal mt-1" style={{ color: 'var(--text-secondary)' }}>
                  Apenas administradores podem editar as informações do grupo
                </p>
              </div>
            </div>

            {/* Mensagens Temporárias */}
            <div>
              {/* MODIFICADO: Label e select com variáveis */}
              <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                Mensagens Temporárias
              </label>
              <select
                value={formData.ephemeralDuration}
                onChange={(e) => setFormData({ ...formData, ephemeralDuration: Number(e.target.value) })}
                className="w-full px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-normal"
                style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
              >
                {EPHEMERAL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs font-normal" style={{ color: 'var(--text-secondary)' }}>
                As mensagens serão automaticamente apagadas após o período selecionado
              </p>
            </div>

            {/* Resumo */}
            {/* MODIFICADO: Resumo com superfícies do tema */}
            {/* ANTES: bg-gray-50 e text-gray-* */}
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
              <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Resumo do Grupo</h4>
              <dl className="space-y-2 text-sm font-normal">
                <div className="flex justify-between">
                  <dt style={{ color: 'var(--text-secondary)' }}>Nome:</dt>
                  <dd className="font-semibold" style={{ color: 'var(--text-primary)' }}>{formData.subject}</dd>
                </div>
                <div className="flex justify-between">
                  <dt style={{ color: 'var(--text-secondary)' }}>Instância:</dt>
                  <dd className="font-semibold" style={{ color: 'var(--text-primary)' }}>{formData.instanceName}</dd>
                </div>
                <div className="flex justify-between">
                  <dt style={{ color: 'var(--text-secondary)' }}>Participantes:</dt>
                  <dd className="font-semibold" style={{ color: 'var(--text-primary)' }}>{formData.participants.length}</dd>
                </div>
                <div className="flex justify-between">
                  <dt style={{ color: 'var(--text-secondary)' }}>Modo Anúncio:</dt>
                  <dd className="font-semibold" style={{ color: 'var(--text-primary)' }}>{formData.isAnnouncement ? 'Sim' : 'Não'}</dd>
                </div>
              </dl>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      {/* MODIFICADO: Footer com variáveis e botão secundário padronizado */}
      {/* ANTES: border-t border-gray-200, botão cinza fixo */}
      <div className="px-6 py-4 border-t flex items-center justify-between" style={{ borderColor: 'var(--border-color)' }}>
        <button
          onClick={step === 1 ? onCancel : prevStep}
          disabled={loading}
          className="px-6 py-2.5 rounded-lg transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = theme === 'dark' ? '#2a2a2f' : '#f3f4f6'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--bg-secondary)'; }}
        >
          <ArrowLeft className="w-4 h-4" />
          {step === 1 ? 'Cancelar' : 'Voltar'}
        </button>

        {step < 3 ? (
          <button
            onClick={nextStep}
            disabled={loading}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            Próximo
            <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Criando...</span>
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Criar Grupo
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default GroupForm;

